/**
 * Brand Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const brandSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  logo_url: z.string().url('URL inválida').optional().or(z.literal('')),
  website: z.string().url('URL inválida').optional().or(z.literal('')),
  is_active: z.boolean().default(true),
});

export default function BrandModal({ open, onOpenChange, brand, onSave, loading }) {
  const isEditing = !!brand?.id;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(brandSchema),
    defaultValues: {
      name: '',
      description: '',
      logo_url: '',
      website: '',
      is_active: true,
    },
  });

  // Reset form when modal opens or brand changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (brand) {
      reset({
        name: brand.name || '',
        description: brand.description || '',
        logo_url: brand.logo_url || '',
        website: brand.website || '',
        is_active: brand.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        description: '',
        logo_url: '',
        website: '',
        is_active: true,
      });
    }
  }, [open, brand, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Marca' : 'Nova Marca'}
      description={isEditing ? 'Atualize as informações da marca' : 'Preencha os dados da nova marca'}
      size="default"
      formId="brand"
      entityId={brand?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText="Salvar"
      cancelText="Cancelar"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Nome da marca"
            data-testid="brand-name"
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Descrição da marca..."
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="logo_url">URL do Logo</Label>
          <Input
            id="logo_url"
            {...register('logo_url')}
            placeholder="https://..."
          />
          {errors.logo_url && (
            <p className="text-sm text-destructive">{errors.logo_url.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            {...register('website')}
            placeholder="https://..."
          />
          {errors.website && (
            <p className="text-sm text-destructive">{errors.website.message}</p>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="is_active"
            checked={watch('is_active')}
            onCheckedChange={(v) => setValue('is_active', v)}
          />
          <Label htmlFor="is_active">Marca ativa</Label>
        </div>
      </div>
    </FormModal>
  );
}
